/* eslint-disable react/sort-comp */
import React, { Component, ErrorInfo } from 'react';
import { BrowserRouter as Router, Switch, Route, RouteComponentProps } from 'react-router-dom';
import { routes, configureRoutes } from 'routes';
import { Provider } from 'react-redux';
import { configureStore } from 'store';

import './BasketCheckoutModule.scss';

import './helpers/arrayExtensions';

import {
    BasketPage,
    CompleteResetPasswordPage,
    DeliveryDetailsPage,
    DeliveryDatePage,
    PaymentPage,
    DataProtectionNoticePage,
    OrderPage,
    FinanceApplicationPage,
    FinanceApplicationStepTwoPage,
    OrderSummaryPage,
    FinanceApplicationStepThreePage,
    FinanceApplicationStepFourPage,
    AddToCartPage
} from 'pages';
import { BasketSnackbarProvider, BasketCheckoutContainer, TitleSetter, BasketRoot } from 'components';
import { logsHelper, errorToBasketErrorModel, userHelper } from 'helpers';
import { exponea } from 'repositories/exponea.repository';

const store = configureStore();

interface HasComponent {
    component: any;
}

interface OwnProps {
    basePath: string;
}

interface OwnState {
    hasUnhandledError: boolean;
}

export default class BasketCheckoutModule extends Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);
        configureRoutes(props.basePath);

        this.state = { hasUnhandledError: false };

        if (userHelper.isUserLoggedIn()) {
            userHelper.removeSessionId();
        }

        // Initialize Exponea
        exponea.initialize();
    }

    static getDerivedStateFromError(): OwnState {
        return { hasUnhandledError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const basketError = errorToBasketErrorModel(error, errorInfo?.componentStack || '');
        logsHelper.error(basketError);
    }

    render(): JSX.Element {
        if (this.state.hasUnhandledError) {
            return (
                <div id='basket-checkout-module' className='basket-checkout-module__error'>
                    <h3>Sorry, something went wrong, please reload the page</h3>
                </div>
            );
        }

        return (
            <div id='basket-checkout-module'>
                <Provider store={store}>
                    <BasketSnackbarProvider>
                        <BasketRoot>
                            <Router>
                                <Switch>
                                    <Route
                                        path={routes.basket.path}
                                        exact={routes.basket.exact}
                                        render={this.withTitle({ component: BasketPage }, routes.basket.title)}
                                    />
                                    <Route
                                        path={routes.addToCart.path}
                                        exact={routes.addToCart.exact}
                                        render={this.withTitle({ component: AddToCartPage }, routes.addToCart.title)}
                                    />
                                    <BasketCheckoutContainer>
                                        <Route
                                            path={routes.deliveryDetails.path}
                                            exact={routes.deliveryDetails.exact}
                                            render={this.withTitle({ component: DeliveryDetailsPage }, routes.deliveryDetails.title)}
                                        />
                                        <Route
                                            path={routes.deliveryDate.path}
                                            exact={routes.deliveryDate.exact}
                                            render={this.withTitle({ component: DeliveryDatePage }, routes.deliveryDate.title)}
                                        />
                                        <Route
                                            path={routes.payment.path}
                                            exact={routes.payment.exact}
                                            render={this.withTitle({ component: PaymentPage }, routes.payment.title)}
                                        />
                                        <Route
                                            path={routes.dataProtectionNotice.path}
                                            exact={routes.dataProtectionNotice.exact}
                                            render={this.withTitle({ component: DataProtectionNoticePage }, routes.dataProtectionNotice.title)}
                                        />
                                        <Route
                                            path={routes.orderPage.path}
                                            exact={routes.orderPage.exact}
                                            render={this.withTitle({ component: OrderPage }, routes.orderPage.title)}
                                        />
                                        <Route
                                            path={routes.financeApplication.path}
                                            exact={routes.financeApplication.exact}
                                            render={this.withTitle({ component: FinanceApplicationPage }, routes.financeApplication.title)}
                                        />
                                        <Route
                                            path={routes.financeApplicationStepTwo.path}
                                            exact={routes.financeApplicationStepTwo.exact}
                                            render={this.withTitle({ component: FinanceApplicationStepTwoPage }, routes.financeApplicationStepTwo.title)}
                                        />
                                        <Route
                                            path={routes.financeApplicationStepThree.path}
                                            exact={routes.financeApplicationStepThree.exact}
                                            render={this.withTitle({ component: FinanceApplicationStepThreePage }, routes.financeApplicationStepThree.title)}
                                        />
                                        <Route
                                            path={routes.financeApplicationStepFour.path}
                                            exact={routes.financeApplicationStepFour.exact}
                                            render={this.withTitle({ component: FinanceApplicationStepFourPage }, routes.financeApplicationStepFour.title)}
                                        />
                                        <Route
                                            path={routes.orderSummaryPage.path}
                                            exact={routes.orderSummaryPage.exact}
                                            render={this.withTitle({ component: OrderSummaryPage }, routes.orderSummaryPage.title)}
                                        />
                                        <Route
                                            path={routes.completeResetPasswordPage.path}
                                            exact={routes.completeResetPasswordPage.exact}
                                            render={this.withTitle({ component: CompleteResetPasswordPage }, routes.completeResetPasswordPage.title)}
                                        />
                                    </BasketCheckoutContainer>
                                </Switch>
                            </Router>
                        </BasketRoot>
                    </BasketSnackbarProvider>
                </Provider>
            </div>
        );
    }

    withTitle = (object: HasComponent, title: string) => (props: RouteComponentProps) => {
        return (
            <>
                <TitleSetter title={title} />
                <object.component {...props} />
            </>
        );
    };
}